import { Alert } from "@mui/material";
import {
  ArrayInput,
  Create,
  Datagrid,
  Edit,
  EditButton,
  ImageField,
  List,
  Loading,
  SaveButton,
  SimpleFormIterator,
  TabbedForm,
  TextField,
  TextInput,
  Toolbar,
  TranslatableInputs,
  useGetList,
  useNotify,
  useTranslatableContext,
} from "react-admin";
import LocalisedDateField from "../components/localisedDateField";
import { SummaryTabHeader } from "../components/SummaryTabHeader";
import { TranslationsTabHeader } from "../components/TranslationsTabHeader";

const SizeDescriptionInputs = () => {
  const { getSource } = useTranslatableContext();

  const base = getSource("translations.sizeDescription");

  const Row = ({ k, label }: { k: "S" | "M" | "L" | "MIXED"; label: string }) => (
    <div style={{ display: "grid", gridTemplateColumns: "90px 1fr", gap: 12, alignItems: "center" }}>
      <strong>{label}</strong>
      <TextInput source={`${base}.${k}`} label="" fullWidth helperText={false} />
    </div>
  );

  return (
    <div style={{ marginTop: 16 }}>
      <div style={{ fontWeight: 600, marginBottom: 8 }}>Size description</div>
      <Row k="S" label="S" />
      <div style={{ height: 8 }} />
      <Row k="M" label="M" />
      <div style={{ height: 8 }} />
      <Row k="L" label="L" />
      <div style={{ height: 8 }} />
      <Row k="MIXED" label="Mixed" />
    </div>
  );
};

const ProductLabelBlurbsInputs = () => {
  const { getSource } = useTranslatableContext();

  const base = getSource("translations.productLabelBlurbs");

  const Row = ({ dentalWork, label }: any) => (
    <div style={{ display: "grid", gridTemplateColumns: "90px 1fr", gap: 12, alignItems: "center" }}>
      <strong>{label}</strong>
      <TextInput source={`${base}.${dentalWork}`} label="" fullWidth helperText={false} />
    </div>
  );

  return (
    <div style={{ marginTop: 16 }}>
      <div style={{ fontWeight: 600, marginBottom: 8 }}>Product Label Blurbs</div>
      <Row dentalWork="default" label="Default" />
      <div style={{ height: 8 }} />
      <Row dentalWork="CrownOrBridges" label="Crown or Bridges" />
      <div style={{ height: 8 }} />
      <Row dentalWork="DentalImplants" label="Dental Implants" />
    </div>
  );
};

export const GeoffreyProductTranslationsForm = () => {
  const { data: locales = [], isLoading, error } = useGetList(
    'locales'
  );
  if (isLoading) { return <Loading />; }
  if (error) { return <p>Error</p> }

  const localeCodes = locales.map((l) => l.pk);

  return (
    <>
      <Alert severity="info">Locales are stored as bcp47 codes (e.g. de-DE / fr-FR / it-IT / en-GB)</Alert>

      <TranslatableInputs locales={localeCodes} defaultLocale={localeCodes[0]} fullWidth sx={{ minWidth: "600px" }}>
        <TextInput source="translations.title" label="Title" fullWidth />
        <TextInput source="translations.productLabel" label="Product Label" fullWidth />
        <ProductLabelBlurbsInputs />

        <TextInput source="translations.description" label="Description" fullWidth multiline />
        <TextInput source="translations.howToText" label="How-to Text" fullWidth multiline />
        <TextInput source="translations.benefitText" label="Benefit Text" fullWidth multiline />
        <SizeDescriptionInputs />
        <TextInput source="translations.ctaText" label="CTA Text" fullWidth />
        <TextInput source="translations.purchaseLink" label="Purchase Link" fullWidth />
      </TranslatableInputs>
    </>
  );
};
const SaveButtonWithMessage = () => {
  const notify = useNotify();
  const onSuccess = (data: any) => {
    notify(`Product recommendation saved. ${data.updateMessage || ""}`, {
      type: data.updateMessage ? "warning" : "success",
    });
  };
  return <SaveButton type="button" mutationOptions={{ onSuccess }} alwaysEnable />;
};

const ProductRecommendationEditToolbar = () => (
  <Toolbar>
    <SaveButtonWithMessage />
  </Toolbar>
);

export const GeoffreyProductRecommendationsEdit = () => (
  <Edit mutationMode="pessimistic" redirect={false}>
    <TabbedForm syncWithLocation={false} toolbar={<ProductRecommendationEditToolbar />}>
      <TabbedForm.Tab label={<SummaryTabHeader />}>
        <TextInput source="id" label="Product ID" disabled fullWidth />
        <TextInput source="name" disabled fullWidth />
        <TextInput source="howToInstructionUrl" fullWidth />
        {/* <TextInput source="sizes" fullWidth /> */}
        <ArrayInput source="sizes" label="Sizes">
          <SimpleFormIterator inline>
            <TextInput source="code" label="Code" sx={{ width: 180 }} helperText="Code like 'S', 'M', 'L'" />
            <TextInput source="color" label="Color" sx={{ width: 180 }} helperText="Hex like #00FFAA" />
            {/* If you want a nicer picker:
                <TextInput source="color" type="color" label="Color" />
                (works in most browsers, but you’ll probably still want to store the hex string)
            */}
          </SimpleFormIterator>
        </ArrayInput>
        <TextInput source="imageLink" fullWidth />
      </TabbedForm.Tab>

      <TabbedForm.Tab label={<TranslationsTabHeader />}>
        <GeoffreyProductTranslationsForm />
      </TabbedForm.Tab>
    </TabbedForm>
  </Edit>
);

export const GeoffreyProductRecommendationsList = () => (
  <List exporter={false}>
    <Datagrid bulkActionButtons={false}>
      <ImageField source="imageLink" label="Image" />
      <TextField source="id" label="Product ID" />
      <TextField source="name" label="Name" />
      <LocalisedDateField field="createdAt" label="Created" />
      <LocalisedDateField field="updatedAt" label="Updated" />
      <EditButton />
    </Datagrid>
  </List>
);

export const GeoffreyProductRecommendationsCreate = () => (
  <Create redirect="edit">
    <TabbedForm syncWithLocation={false}>
      <TabbedForm.Tab label={<SummaryTabHeader />}>
        <TextInput source="id" label="Product ID" disabled fullWidth />
        <TextInput source="name" fullWidth />
        <TextInput source="howToInstructionUrl" fullWidth />
        <TextInput source="sizes" fullWidth />
        <TextInput source="imageLink" fullWidth />
      </TabbedForm.Tab>
    </TabbedForm>
  </Create>
);
